import React, {RefObject, useCallback, useEffect} from 'react';
import {createPortal} from 'react-dom';
import QrZoomClose from './QrZoomClose';
import QrZoomContent from './QrZoomContent';
import * as css from './QrZoom.css';

interface QrZoomProps {
    barcode: string;
    qid: number;
    ticketLabel?: string;
    canceled?: boolean;
    scanned: boolean;
    scanTs?: number;
    isScanning: boolean;
    isPop: boolean;
    zoomQrRef: RefObject<HTMLDivElement>;
    reduceMotion: boolean | null;
    onClose: () => void;
}

function QrZoom({
    barcode,
    qid,
    ticketLabel,
    canceled,
    scanned,
    scanTs,
    isScanning,
    isPop,
    zoomQrRef,
    reduceMotion,
    onClose,
}: QrZoomProps): React.ReactElement {
    const transition = {
        duration: reduceMotion ? 0 : 0.25,
        ease: [0.25, 0.1, 0.25, 1] as const,
    };

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if(e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return createPortal(
        <css.overlay
            initial={{opacity: reduceMotion ? 1 : 0}}
            animate={{opacity: 1}}
            transition={transition}
            onClick={onClose}
            data-cy="qr-zoom"
            role="dialog"
            aria-modal
        >
            <css.inner
                initial={{scale: reduceMotion ? 1 : 0.92}}
                animate={{scale: 1}}
                transition={transition}
                onClick={e => e.stopPropagation()}
            >
                <QrZoomContent
                    barcode={barcode}
                    qid={qid}
                    ticketLabel={ticketLabel}
                    reduceMotion={reduceMotion}
                    canceled={canceled}
                    scanned={scanned}
                    scanTs={scanTs}
                    isScanning={isScanning}
                    isPop={isPop}
                    zoomQrRef={zoomQrRef}
                />
            </css.inner>
            <QrZoomClose reduceMotion={reduceMotion} onClose={onClose} />
        </css.overlay>,
        document.body,
    ) as React.ReactElement;
}

export default QrZoom;
